/**
 * Wohin die Vorschau-Karte gehört (PO 31.08.2026: „die vorschau pdf mail ist
 * abgehackt weil der block ganz unten ist und immer oben rechts, sollte am
 * cursor gezeigt werden, und dann dadrüber").
 *
 * Die alte Regel setzte die Karte NEBEN den Anker und rechnete mit einer festen
 * Höhe von 220 px. Steht die Zeile unten im Fenster, ist eine Karte mit
 * PDF-Seite oder Mailtext aber gut doppelt so hoch — sie lief unten aus dem
 * Bild und war abgeschnitten. Jetzt hängt die Karte am ZEIGER und geht nach
 * oben auf: dorthin, wo beim Überfahren einer Liste immer Platz ist.
 *
 * Rein und DOM-frei, damit der Node-Runner sie prüft; das Messen bleibt in der
 * Komponente.
 */

export interface Rechteck {
    top: number;
    left: number;
    right: number;
    bottom: number;
}

export interface Zeiger {
    x: number;
    y: number;
}

export interface PlatzEingabe {
    /** Letzte Zeigerposition — `null` auf dem Tastaturweg (Fokus statt Hover). */
    zeiger: Zeiger | null;
    /** Rechteck des Ankers; trägt die Platzierung, wenn es keinen Zeiger gibt. */
    anker: Rechteck;
    breite: number;
    /**
     * NATÜRLICHE Höhe der Karte — die, die sie ohne Deckel hätte. Wird hier die
     * bereits gekappte Höhe hereingereicht, kippt die Karte zwischen oben und
     * unten hin und her: gekappt passt sie nach oben, ungekappt nicht.
     */
    hoehe: number;
    fenster: { breite: number; hoehe: number };
    abstand?: number;
    rand?: number;
}

export interface Platz {
    top: number;
    left: number;
    /** Was an der gewählten Seite ins Fenster passt — die Karte kappt darüber. */
    maxHoehe: number;
    /** Zur Prüfung und für die Pfeilrichtung: liegt die Karte über dem Zeiger? */
    nachOben: boolean;
}

/** Luft zwischen Zeiger und Karte — genug, dass der Zeiger frei bleibt. */
export const HK_ABSTAND = 14;
/** Sicherheitsabstand zum Fensterrand. */
export const HK_RAND = 8;

export function platziereKarte({
    zeiger,
    anker,
    breite,
    hoehe,
    fenster,
    abstand = HK_ABSTAND,
    rand = HK_RAND,
}: PlatzEingabe): Platz {
    // Ohne Zeiger gibt es keinen Punkt, an dem die Karte hängen könnte — dann
    // tritt der Anker an seine Stelle: waagerecht mittig, senkrecht an seinen
    // Kanten, damit die Karte die Zeile selbst nicht verdeckt.
    const x = zeiger ? zeiger.x : (anker.left + anker.right) / 2;
    const yOben = zeiger ? zeiger.y : anker.top;
    const yUnten = zeiger ? zeiger.y : anker.bottom;

    const platzOben = Math.max(0, yOben - abstand - rand);
    const platzUnten = Math.max(0, fenster.hoehe - yUnten - abstand - rand);

    // Bevorzugt ÜBER dem Zeiger. Nach unten nur, wenn die Karte oben nicht
    // ganz hinpasst UND unten mehr Platz ist — sonst wäre der Wechsel ein
    // Rückschritt.
    const nachOben = hoehe <= platzOben || platzOben >= platzUnten;
    const maxHoehe = nachOben ? platzOben : platzUnten;
    const gezeigteHoehe = Math.min(hoehe, maxHoehe);
    const top = nachOben ? yOben - abstand - gezeigteHoehe : yUnten + abstand;

    // Waagerecht am Zeiger zentriert, aber vollständig im Fenster. Ist das
    // Fenster schmaler als die Karte, gewinnt der linke Rand.
    const maxLinks = Math.max(rand, fenster.breite - breite - rand);
    const left = Math.min(Math.max(rand, x - breite / 2), maxLinks);

    return {
        // Gerundet: sonst löst ein Bruchteil eines Pixels beim Nachmessen eine
        // weitere Runde aus.
        top: Math.round(Math.max(rand, top)),
        left: Math.round(left),
        maxHoehe: Math.round(maxHoehe),
        nachOben,
    };
}
